export default {
  common: { continue: 'Continue', back: 'Back', skip: 'Skip', save: 'Save', cancel: 'Cancel', loading: 'Loading…', error: 'Something went wrong. Try again.', required: 'Required', brandName: 'ininvoice', copyright: '© 2026 ININVOICE. All rights reserved.' },
  brand: { name: 'ininvoice', tagline: 'Pay only what you ordered and received.' },
  auth: {
    login: { title: 'Sign in', email: 'Email', password: 'Password', submit: 'Sign in', forgot: 'Forgot your password?', noAccount: 'No account yet?', createAccount: 'Create account', googleSignIn: 'Continue with Google', or: 'or' },
    forgot: { title: 'Reset password', hint: 'We will email you a reset link.', submit: 'Send link', backToLogin: 'Back to sign in', sent: 'Email sent. Check your inbox.' },
    reset: { title: 'New password', newPassword: 'New password', confirmPassword: 'Confirm password', submit: 'Save password', success: 'Password updated. Sign in again.', mismatch: 'Passwords do not match' },
    callback: { processing: 'Signing you in…' },
  },
  invitation: {
    heading: "You have been invited",
    headingSignIn: "Sign in to accept the invitation",
    title: "Invitation to {org}",
    by: "{who} invites you to approve invoices for {org} on ininvoice. Create your account with this email, or sign in if you already have one.",
    anonymous: "You are invited to approve invoices for {org} on ininvoice. Create your account with this email, or sign in if you already have one.",
    emailLocked: "The invitation is for this email.",
    dead: "This invitation does not exist, was already used or was withdrawn. Ask whoever invited you for a new one.",
  },
  signupHero: {
    email: 'Your work email',
    submit: 'Continue',
    haveAccount: 'Already have an account?',
    signIn: 'Sign in',
    emailRequired: 'Email is required',
    emailInvalid: 'Invalid email',
  },
  requestAccess: {
    title: 'Request access',
    body: 'ininvoice is invitation-only. If someone at your company already uses it, ask them to invite you; otherwise tell us what you need and we will give you access.',
    cta: 'Request access',
    haveAccount: 'Already have an account?',
    signIn: 'Sign in',
  },
  signin: {
    title: 'Welcome to ininvoice',
    google: 'Continue with Google',
    googleLoading: 'Connecting…',
    or: 'or',
    email: 'Email',
    password: 'Password',
    forgot: 'Forgot your password?',
    submit: 'Sign in',
    noAccess: 'Your company does not use ininvoice yet?',
    requestAccess: 'Request access',
    invited: 'First time with this invitation?',
    createAccount: 'Create your account',
    emailRequired: 'Email is required',
    emailInvalid: 'Invalid email',
    passwordRequired: 'Password is required',
    errors: {
      credentials: 'Incorrect email or password. If you sign in with Google, use “Continue with Google”.',
      tooMany: 'Too many attempts. Wait a few minutes and try again.',
      tooManySeconds: 'Too many attempts. Try again in {seconds} s.',
      captcha: 'The security check is invalid or has expired. Complete it again and retry.',
      generic: 'We could not sign you in. Try again in a few seconds.',
    },
  },
  signup: {
    title: 'Create your account',
    subtitle: 'Get started in minutes.',
    email: 'Work email',
    password: 'Password',
    passwordHint: 'At least 8 characters, one uppercase, one number.',
    accept: 'I accept the {terms} and the {privacy}.',
    termsLink: 'terms of service',
    privacyLink: 'privacy policy',
    submit: 'Create account',
    haveAccount: 'Already have an account?',
    signIn: 'Sign in',
    rules: {
      emailRequired: 'Email is required',
      emailInvalid: 'Invalid email',
      passwordRequired: 'Password is required',
      passwordMin: 'At least 8 characters',
      passwordUpper: 'Must contain at least one uppercase letter',
      passwordDigit: 'Must contain at least one number',
      mustAccept: 'Accept the terms to continue',
    },
    errors: {
      alreadyHaveAccount: 'If you already have an account, sign in with your email and password.',
      passwordWeak: 'Password does not meet the requirements',
      captcha: 'The security check is invalid or has expired. Complete it again and retry.',
      tooMany: 'Too many attempts. Wait a few minutes and try again.',
      session: 'Your account is created, but we could not sign you in. Sign in with your email and password.',
      generic: 'We could not create the account. Try again in a few seconds.',
    },
  },
  forgot: {
    title: 'Reset your password',
    hint: 'Enter your email and we will send you a link to choose a new one.',
    email: 'Email',
    submit: 'Send link',
    sentTitle: 'Check your inbox',
    sentBody: 'If there is an account for {email}, a link to reset the password will arrive in a few minutes.',
    backToSignIn: 'Back to sign in',
    emailRequired: 'Email is required',
    emailInvalid: 'Invalid email',
    errors: {
      tooMany: 'Too many requests. Wait a few minutes and try again.',
      tooManySeconds: 'Too many requests. Try again in {seconds} s.',
      captcha: 'The security check is invalid or has expired. Complete it again and retry.',
    },
  },
  turnstile: {
    failed: 'We could not load the security check. Reload the page.',
  },
  qualify: {
    title: 'Set up your account',
    subtitle: 'We tune the app to your case. 30 seconds.',
    progress: 'Step {current} of {total}',
    next: 'Continue', back: 'Back', skip: 'Skip', finish: 'Enter the app',
    error: 'Could not save. Retrying…',
    steps: {
      role:        { title: 'What is your role?', hint: 'We use it to surface what matters first.' },
      companySize: { title: 'How big is the company?', hint: 'Total employees, not only finance.' },
      volume:      { title: 'How many invoices per month?', hint: 'Rough number. Helps us match the right plan.' },
      erp:         { title: 'What do you use for accounting today?', hint: 'Pick all that apply. If none, pick Excel.' },
      pain:        { title: 'What brings you here?', hint: 'Pick the main one. Everything else later.' },
      callback:    { title: 'Prefer a quick call', hint: 'We call back today if you need it.', phoneLabel: 'Phone', wantsCall: 'I want a call to see it live' },
    },
    options: {
      role: { cfo: 'CFO / Finance Director', ap_manager: 'AP Manager', controller: 'Controller / Admin Lead', founder_ceo: 'Founder / CEO', accountant_asesoria: 'Accountant / Bookkeeping firm', bookkeeper: 'In-house bookkeeper', other: 'Other' },
      companySize: { '1-9': '1-9 employees', '10-49': '10-49 employees', '50-249': '50-249 employees', '250+': '250+ employees' },
      volume: { lt_50: 'Less than 50 invoices/mo', '50_150': '50-150 invoices/mo', '150_300': '150-300 invoices/mo', '300_1000': '300-1,000 invoices/mo', '1000_plus': 'More than 1,000 invoices/mo' },
      erp: { holded: 'Holded', quickbooks: 'QuickBooks', xero: 'Xero', sage50: 'Sage 50', sage200: 'Sage 200', a3: 'A3 ERP', sap: 'SAP', quipu: 'Quipu', anfix: 'Anfix', contasol: 'ContaSol', excel_none: 'Excel / None', other: 'Other' },
      pain: { manual_entry_time: 'Too much time on manual entry', pricing_errors: 'Pricing errors on invoices', duplicate_payments: 'Duplicate payments', audit_compliance: 'Audit / compliance', growth_scaling: 'Scaling the AP team', fraud_risk: 'Vendor fraud risk' },
    },
  },
  validation: { required: 'Required', email: 'Invalid email', minLength: 'At least {n} characters', phone: 'Invalid phone' },
  langSelector: { label: 'Language', es: 'Español', en: 'English', ca: 'Català' },
};
